import Button from 'assets/components/button/Button';
import LinkButton from 'assets/components/button/LinkButton';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Card from 'assets/components/card/Card';
import ThemeCardRow from 'assets/components/card/themes/Theme.Card.Row';
import Collection from 'assets/components/data/Collection';
import ThemeCollectionTable from 'assets/components/data/themes/Theme.Collection.Table';
import DataMap from 'assets/components/dataMap/DataMap';
import useForm from 'assets/components/form/hooks/Form';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import FileInput from 'assets/components/inputs/file/FileInput';
import ThemeFileInputDropzone from 'assets/components/inputs/file/themes/Theme.FileInput.Dropzone';
import MultilineTextInput from 'assets/components/inputs/multilineText/MultilineTextInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import ErrorWrapper from 'assets/components/inputs/wrappers/ErrorWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import ThemeWrapperPaper from 'assets/themes/wrappers/Theme.Wrapper.Paper';
import { combineStrings } from 'assets/utils/data/String';
import contractRefundDocumentDeleteApi from 'models/sales/contractRefundDocument/delete/Api.ContractRefundDocument.Delete';
import contractRefundDocumentSaveApi from 'models/sales/contractRefundDocument/save/Api.ContractRefundDocument.Save';
import { useEffect, useMemo } from 'react';
import { useContractRefundProvider } from '../../Sales.ContractRefund.Hooks';

const SalesContractRefundInfoDocuments = () => {
  const { lang } = getLocales();
  const { selected, findApi, permissions } = useContractRefundProvider();

  const { execute: deleteDoc } = useApi({
    action: contractRefundDocumentDeleteApi,
    wait: true,
    callback: () => findApi.execute((b) => b),
  });
  const { execute: saveDoc, isExecuting: isSavingDoc } = useApi({
    action: contractRefundDocumentSaveApi,
    wait: true,
    callback: () => findApi.execute((b) => b),
  });
  const onDelete = (documentId: number) => deleteDoc({ id: selected?.id, documentId });

  const docForm = useForm<Utils.FormData<Model.IContractRefundDocument>>({
    default: {
      contractRefundId: selected?.id,
    },
    validation: (data, errors) => {
      if (!data.formFile) errors.formFile = lang.required;
    },
    onSubmit: saveDoc,
  });
  useEffect(() => {
    if (docForm?.data?.contractRefundId !== selected?.id) {
      docForm.update({ contractRefundId: selected?.id });
    }
  }, [selected?.id]);
  const formFile = useMemo(
    () =>
      docForm.data.formFile && {
        original: docForm.data.formFile,
        type: docForm.data.formFile.type as Utils.File.MimeType,
        dataUrl: window.URL.createObjectURL(docForm.data.formFile),
      },
    [docForm.data.formFile]
  );

  return (
    <>
      <Collection class={combineStrings(' ', ThemeCollectionTable, ThemeWrapperPaper)} style={{ padding: 0 }}>
        <div
          style={{
            fontSize: Theme.FontSize.L,
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {lang.documents}
        </div>
        {!selected.contractRefundDocuments?.length && <ErrorWrapper children={lang.noFiles} />}
        {selected?.documentationAggregate && (
          <Card
            class={ThemeCardRow}
            info={lang.refundDocument}
            style={{ borderTop: 'var(--sizeXXS) solid var(--colorShadow)' }}
            title={selected?.documentationAggregate?.name}
            media={<Icon class="fas-folder" />}
            actions={
              <>
                <LinkButton
                  class={ThemeButtonCircle}
                  url={selected?.documentationAggregate?.url}
                  media={<Icon class="fas-download" />}
                  target="_blank"
                  htmlElementProps={{ title: lang.download }}
                />
              </>
            }
          />
        )}
        <DataMap
          data={selected.contractRefundDocuments}
          render={({ data: doc, index }) => (
            <Card
              key={index}
              class={ThemeCardRow}
              title={doc.name}
              info={
                <>
                  {lang.uploadedBy} {doc.createdBy?.fullName ?? lang.unknownUser} {lang.at}{' '}
                  <FormatValue value={doc.createdAtUtc} />
                </>
              }
              subtitle={combineStrings(': ', lang.notes, doc.notes)}
              media={<Icon class="fas-file-upload" />}
              actions={
                <>
                  <LinkButton
                    class={ThemeButtonCircle}
                    url={doc.mediaFile?.url}
                    media={<Icon class="fas-download" />}
                    target="_blank"
                    disabled={!doc.id}
                    htmlElementProps={{ title: lang.download }}
                  />
                  <Button
                    class={ThemeButtonCircle}
                    onClick={onDelete}
                    data={doc.id}
                    media={<Icon class="fas-trash" />}
                    disabled={!doc.id}
                    htmlElementProps={{ title: lang.delete }}
                  />
                </>
              }
            />
          )}
        />
      </Collection>
      {permissions.EDIT_CONTRACTREFUND && (
        <>
          <br />
          <FormWrapper class={ThemeWrapperPaper}>
            <h3>{lang.documentManagement}</h3>
            <TextInput
              name="name"
              label={lang.name}
              value={docForm.data.name}
              onChange={docForm.update}
              error={docForm.errors.name}
            />
            <MultilineTextInput
              name="notes"
              label={lang.notes}
              value={docForm.data.notes}
              onChange={docForm.update}
              error={docForm.errors.notes}
            />
            <FileInput
              name="file"
              fileLimit={1}
              value={formFile ? [formFile] : []}
              onChange={({ file }) => {
                const formFile = file && file[0]?.original;
                docForm.update({ formFile });
              }}
              class={ThemeFileInputDropzone}
              error={docForm.errors.formFile?._objectError}
            />
            <Button
              render={lang.add}
              media={<Icon class="fas-upload" />}
              onClick={docForm.submit}
              disabled={docForm.hasErrors}
              isLoading={docForm.isSubmitted || isSavingDoc}
              active={!docForm.hasErrors}
              class={ThemeButtonCircle}
              htmlElementProps={{ title: lang.add }}
            />
          </FormWrapper>
        </>
      )}
    </>
  );
};

export default SalesContractRefundInfoDocuments;
